/**
 * Typed wrapper around react-i18next's useTranslation hook.
 *
 * i18next v25 introduced strict TypeScript types that require a
 * `CustomTypeOptions` module augmentation to accept namespace arguments.
 * CRA's type checker cannot process that augmentation reliably (ambient
 * `declare module` replaces instead of augmenting; module-scoped
 * augmentations aren't loaded by CRA's fork-ts-checker).
 *
 * This wrapper provides type-safe namespace selection without
 * fighting the type system. Import this instead of react-i18next
 * when you need to specify a namespace.
 *
 * Usage:
 *   import { useAppTranslation } from '../i18n/useAppTranslation';
 *   const { t } = useAppTranslation('discover');
 *
 * DEV-963. After the TypeScript 5.4 upgrade, i18next 25.x's strict
 * `t()` signature only accepts (key, options) — not the legacy
 * (key, defaultValue) or (key, defaultValue, options) positional
 * forms that ~210 callers across the codebase use. Rather than
 * mass-rewriting every call site, this wrapper widens t() to accept
 * all four shapes and translates the legacy positional default
 * into the new `{ defaultValue, ...options }` form before delegating
 * to react-i18next's t. Behaviorally identical; just absorbs the
 * shape change inside the wrapper.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type AppNamespace =
  | 'common'
  | 'leftNav'
  | 'userProfile'
  | 'theme'
  | 'discover'
  | 'projectConfig'
  | 'community'
  | 'geography'
  | 'kpi'
  | 'pages'
  | 'chat'
  | 'landing'
  | 'copilot'
  | 'projectTeam'
  // DEV-963. 'content' namespace was missing — caused TS2345 errors
  // in TemplateSelector and Content pages.
  | 'content';

/**
 * Overloaded t() signature accepting all four legacy + modern call shapes:
 *   t('key')                                — namespace lookup
 *   t('key', 'Default text')                — positional default
 *   t('key', { var: 'x' })                  — interpolation options
 *   t('key', 'Default {{var}}', { var:'x' }) — positional default + options
 */
export interface TranslateFn {
  (key: string): string;
  (key: string, defaultValue: string): string;
  (key: string, options: Record<string, unknown>): string;
  (key: string, defaultValue: string, options: Record<string, unknown>): string;
}

interface AppTranslationResult {
  t: TranslateFn;

  i18n: any;
  ready: boolean;
}

export function useAppTranslation(ns?: AppNamespace | AppNamespace[]): AppTranslationResult {
  // Cast through `any` to bypass i18next v25's strict 0-arg signature.
  // At runtime, react-i18next's useTranslation always accepts a namespace string.
  const result = (useTranslation as any)(ns);
  const rawT = result.t;

  // Adapter: accept legacy positional-default shape, rewrite to options
  // form before delegating. No-op when caller already uses the modern
  // options-only or no-args form.
  //
  // Memoized via useCallback so the returned `t` reference is stable
  // across renders. Without this, every consumer that puts `t` in a
  // useEffect/useCallback dep array refires on every parent render —
  // observed as a fetch-storm render loop in CommunitySchemasTab
  // (Rotary International → Schemas tab) saturating Chrome's socket
  // pool with ERR_INSUFFICIENT_RESOURCES. Stability rule: `t` only
  // changes when `rawT` does (locale switch or i18next ready toggle).
  const t = useCallback<TranslateFn>(
    ((
      key: string,
      second?: string | Record<string, unknown>,
      third?: Record<string, unknown>,
    ): string => {
      if (typeof second === 'string') {
        // Legacy positional default. Merge with optional third-arg options.
        const options = third ? { ...third, defaultValue: second } : { defaultValue: second };
        return rawT(key, options);
      }
      if (second && typeof second === 'object') {
        // Modern options-only form. Pass through.
        return rawT(key, second);
      }
      return rawT(key);
    }) as TranslateFn,
    [rawT],
  );

  return { t, i18n: result.i18n, ready: result.ready };
}
